import {
  FailedDependencyLink,
  TaskTemplate,
  TaskTemplateDependency,
  TaskTemplateDependencyDirection,
  TaskTemplateDependencyEdge,
  TaskTemplateDependencyFailure,
} from 'utils/types/taskTemplate';

type V3TaskTemplateResource = {
  id: string;
  attributes: {
    name: string;
    description?: string;
    estimatedHours?: string;
    type?: string;
    startDay?: number | string | null;
    duration?: number | string | null;
    successorIds?: string[];
    predecessorIds?: string[];
  };
};

export function mapV3TemplatesToTaskTemplates(
  data: V3TaskTemplateResource[],
  bundleId: string
): TaskTemplate[] {
  return (data ?? []).map((resource) => ({
    id: resource.id,
    name: resource.attributes.name,
    description: resource.attributes.description,
    estimated_hours: resource.attributes.estimatedHours,
    type: resource.attributes.type,
    task_bundle_id: bundleId,
    start_day:
      resource.attributes.startDay != null
        ? String(Number(resource.attributes.startDay) - 1)
        : undefined,
    duration:
      resource.attributes.duration != null
        ? String(resource.attributes.duration)
        : undefined,
  }));
}

export const generateTaskTemplateDependencyEdges = (
  bundleId: string,
  taskTemplateId: string,
  direction: TaskTemplateDependencyDirection,
  dependencies: TaskTemplateDependency[]
): TaskTemplateDependencyEdge[] => {
  return dependencies.map((dependency) => ({
    id:
      direction === 'predecessors'
        ? `${dependency.id}-${taskTemplateId}`
        : `${taskTemplateId}-${dependency.id}`,
    dependentId: direction === 'predecessors' ? taskTemplateId : dependency.id,
    dependencyId: direction === 'successors' ? taskTemplateId : dependency.id,
    bundleId,
  }));
};

export const mapFailedDependencyLinks = (
  bundleId: string,
  taskTemplateId: string,
  direction: TaskTemplateDependencyDirection,
  failures: TaskTemplateDependencyFailure[]
): FailedDependencyLink[] => {
  if (failures.length === 0) {
    return [];
  }
  return [
    {
      bundleId,
      taskTemplateId,
      direction,
      failures,
    },
  ];
};

export const mergeTemplateDependencies = (
  templateId: string,
  currentEdges: TaskTemplateDependencyEdge[],
  newEdges: TaskTemplateDependencyEdge[]
) => {
  const otherEdges = currentEdges.filter(
    (edge) =>
      edge.dependentId !== templateId && edge.dependencyId !== templateId
  );
  return [...otherEdges, ...newEdges];
};

export const removeDependencyEdges = (
  edges: TaskTemplateDependencyEdge[],
  taskTemplateId: string,
  direction: TaskTemplateDependencyDirection,
  dependencies: string[]
) => {
  return edges.filter((edge) => {
    if (direction === 'predecessors' && edge.dependentId === taskTemplateId) {
      return !dependencies.includes(edge.dependencyId);
    }
    if (direction === 'successors' && edge.dependencyId === taskTemplateId) {
      return !dependencies.includes(edge.dependentId);
    }
    return true;
  });
};

export const extractDependencyEdgesFromV3Response = (
  templates: V3TaskTemplateResource[],
  bundleId: string
): TaskTemplateDependencyEdge[] => {
  return templates.flatMap((template) => {
    if (!template.attributes.successorIds?.length) return [];
    return generateTaskTemplateDependencyEdges(
      bundleId,
      template.id,
      'successors',
      template.attributes.successorIds.map(
        (id) => ({ id } as TaskTemplateDependency)
      )
    );
  });
};
